import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import { useEffect, useId, useState } from 'react';
import { Form } from 'react-bootstrap';
import '../../../../assets/floodSettingsField.css';
import HelpHint from '../../../../components/HelpHint/HelpHint';
import {
  DURATION_UNITS,
  type DurationUnit,
  displayValue,
  durationToMs,
  msToDuration,
  unitToMs,
} from '../../utils/duration.utils';

const DURATION_UNIT_OPTIONS = DURATION_UNITS.map(({ unit, label }) => ({ value: unit, label }));

/**
 * A milliseconds field the operator reads in seconds, minutes or hours. The
 * stored value stays milliseconds (the backend contract); the unit is a view.
 * Changing the unit keeps the typed NUMBER (5 min → 5 h means five hours) —
 * that is what a unit picker means everywhere else. When the stored value
 * changes underneath (another device selected) the unit re-derives so 900000
 * shows as 15 minutes, never 900 seconds.
 */
export default function DurationField({
  label,
  help,
  ms,
  minMs,
  onMsChange,
}: {
  label: string;
  help?: string;
  ms: number | null | undefined;
  /** The smallest duration the field accepts, in ms (shown in the unit). */
  minMs?: number;
  onMsChange: (ms: number) => void;
}) {
  const natural = msToDuration(ms)?.unit ?? 's';
  const [unit, setUnit] = useState<DurationUnit>(natural);
  const inputId = useId();

  // A new stored value (device switch, external save) re-derives the unit
  // when the current one would show a fraction of what is a clean duration.
  useEffect(() => {
    const n = Number(ms);
    if (!Number.isFinite(n) || n <= 0) return;
    if (n % unitToMs(unit) !== 0) setUnit(msToDuration(n)?.unit ?? 's');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ms]);

  const value = displayValue(ms, unit);
  const min = minMs != null ? displayValue(minMs, unit) : undefined;

  return (
    <div className="row align-items-center g-2 flood-settings-field">
      <div className="col-lg-5">
        <Form.Label htmlFor={inputId} className="mb-0">
          {label}
          {help ? <HelpHint text={help} /> : null}
        </Form.Label>
      </div>
      <div className="col-lg-7 d-flex gap-2">
        <Form.Control
          id={inputId}
          className="flood-settings-control"
          type="number"
          value={value}
          min={min === '' ? undefined : min}
          step="any"
          onChange={(e) => {
            const next = durationToMs(e.target.value, unit);
            if (next != null) onMsChange(next);
          }}
        />
        <BaseSelect
          wrapperClassName="flex-shrink-0"
          isSearchable={false}
          classNames={{ control: () => 'flood-settings-control' }}
          styles={{ container: (base) => ({ ...base, width: 'auto', minWidth: 96 }) }}
          selectProps={{
            'aria-label': `${label} unit`,
            options: DURATION_UNIT_OPTIONS,
            value: findSelectOption(DURATION_UNIT_OPTIONS, unit),
            onChange: (option: { value: DurationUnit } | null) => {
              if (!option) return;
              const nextUnit = option.value;
              setUnit(nextUnit);
              // keep the typed number: 5 minutes becomes 5 hours
              const keep = durationToMs(value === '' ? null : value, nextUnit);
              if (keep != null) onMsChange(keep);
            },
          }}
        />
      </div>
    </div>
  );
}
